import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';

interface Assignment {
  id: number;
  cycle_name: string;
  template_name: string;
  about_role: string;
  subject_id: number | null;
  subject_name: string | null;
  status: 'pending' | 'draft' | 'submitted';
  submitted_at: string | null;
}

interface TeamMember {
  user: { id: number; full_name: string; org_layer: string };
  assignments: { template_name: string; status: string }[];
}

const statusLabel: Record<string, string> = {
  pending: 'טרם התחיל',
  draft: 'טיוטה שמורה',
  submitted: 'הוגש',
};

const statusClass: Record<string, string> = {
  pending: 'badge-pending',
  draft: 'badge-draft',
  submitted: 'badge-submitted',
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [teamStatus, setTeamStatus] = useState<TeamMember[]>([]);
  const [expandedMember, setExpandedMember] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/assignments/my');
        setAssignments(data);

        if ((user?.org_layer === 'manager' || user?.org_layer === 'ceo') && user?.id) {
          const { data: team } = await api.get(`/assignments/team/${user.id}`);
          setTeamStatus(team);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  if (loading) return <div className="loading"><div className="spinner" />טוען...</div>;

  const activeCycle = assignments[0]?.cycle_name;
  const pending = assignments.filter(a => a.status !== 'submitted');
  const submitted = assignments.filter(a => a.status === 'submitted');

  return (
    <div className="page page-narrow" style={{ maxWidth: 720 }}>
      <div className="page-header">
        <h1>שלום, {user?.full_name}</h1>
        {activeCycle ? (
          <p className="text-muted">תהליך פעיל: {activeCycle}</p>
        ) : (
          <p className="text-muted">אין תהליך פעיל כרגע</p>
        )}
      </div>

      {assignments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <h3>אין שאלונים משויכים אליך</h3>
          <p>כאשר מנהל המערכת יפעיל תהליך חדש, השאלונים שלך יופיעו כאן.</p>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div style={{ marginBottom: '28px' }}>
              <h2 style={{ marginBottom: '14px' }}>שאלונים למילוי</h2>
              {pending.map(a => (
                <div key={a.id} className="assignment-item">
                  <div className="assignment-item-info">
                    <div className="assignment-item-title">{a.template_name}</div>
                    {a.subject_name && (
                      <div className="assignment-item-sub">עבור: {a.subject_name}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <span className={`badge ${statusClass[a.status]}`}>{statusLabel[a.status]}</span>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => navigate(`/questionnaire/${a.id}`)}
                    >
                      {a.status === 'draft' ? 'המשך' : 'התחל'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {submitted.length > 0 && (
            <div>
              <h2 style={{ marginBottom: '14px' }}>שאלונים שהוגשו</h2>
              {submitted.map(a => (
                <div key={a.id} className="assignment-item" style={{ opacity: 0.7 }}>
                  <div className="assignment-item-info">
                    <div className="assignment-item-title">{a.template_name}</div>
                    {a.subject_name && (
                      <div className="assignment-item-sub">עבור: {a.subject_name}</div>
                    )}
                    {a.submitted_at && (
                      <div className="assignment-item-sub">
                        הוגש: {new Date(a.submitted_at).toLocaleDateString('he-IL')}
                      </div>
                    )}
                  </div>
                  <span className={`badge ${statusClass[a.status]}`}>{statusLabel[a.status]}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {teamStatus.length > 0 && (
        <div style={{ marginTop: '36px' }}>
          <h2 style={{ marginBottom: '4px' }}>סטטוס הצוות שלי</h2>
          <p className="text-muted text-sm" style={{ marginBottom: '14px' }}>מצב המילוי של העובדים הישירים שלך. לחץ/י על שם כדי לראות את פירוט השאלונים.</p>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>שם</th>
                  <th>התקדמות</th>
                  <th>טרם הושלמו</th>
                </tr>
              </thead>
              <tbody>
                {teamStatus.map(member => {
                  const total = member.assignments.length;
                  const done = member.assignments.filter(a => a.status === 'submitted').length;
                  const open = member.assignments.filter(a => a.status !== 'submitted');
                  const expanded = expandedMember === member.user.id;
                  return (
                    <React.Fragment key={member.user.id}>
                      <tr onClick={() => setExpandedMember(expanded ? null : member.user.id)} style={{ cursor: 'pointer' }}>
                        <td style={{ fontWeight: 600 }}>{member.user.full_name}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div className="progress-bar" style={{ width: '90px' }}><div className="progress-bar-fill" style={{ width: `${total ? done / total * 100 : 0}%` }} /></div>
                            <span className="text-sm text-muted">{done}/{total}</span>
                            {total > 0 && done === total && <span className="badge badge-submitted">הושלם</span>}
                          </div>
                        </td>
                        <td className="text-sm text-muted">{open.length === 0 ? '—' : open.map(a => a.template_name).join(' · ')}</td>
                      </tr>
                      {expanded && member.assignments.map((a, i) => (
                        <tr key={`${member.user.id}-${i}`} style={{ background: 'var(--cyan-50)' }}>
                          <td></td>
                          <td className="text-sm">{a.template_name}</td>
                          <td><span className={`badge ${statusClass[a.status]}`}>{statusLabel[a.status]}</span></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
